import { gql, useQuery } from '@apollo/client';
import { Dropdown, FormField, Input, Radio } from 'semantic-ui-react';

import {
    buildIdentifier,
    chooseCodingCommunity,
    communityGroupOf,
    parseFormula,
    type CodingChoice,
    type CommunityOption,
    type FormulaPart,
} from '../../../utils/personIdentifier';

/**
 * One picker for every community a person belongs to, with the identifier worked out from it.
 *
 * **Replaces two boxes.** The first build had "the" community (which coded the identifier) and
 * "also part of these" beneath it, with a hint saying a location should go in the first. Centre
 * managers put Joyya Goods first and people were coded JG. Now there is no first box to get wrong:
 * the location among whatever is picked codes the identifier, in any order — see
 * `utils/personIdentifier.ts`, and the server's copy of the rule which refuses a save that
 * ignores it.
 *
 * The classic form still uses `PersonIdPicker`, unchanged.
 */

const GET_FORMULA = gql`
query RegistrationIdentifierFormula {
    me {
        organisation {
            personIdentifierFormula
        }
    }
}`;

const GET_COMMUNITIES = gql`
query RegistrationCommunities($groupId: Int!) {
    tagsForGroup(groupId: $groupId) {
        id
        name
        code
        isLocation
    }
}`;

const GET_KEY_COUNTERS = gql`
query RegistrationKeyCounters($groupId: Int!) {
    keyCounterLookup(groupId: $groupId) {
        key
        next
    }
}`;

export interface CommunityData {
    loading: boolean;
    /** Null when the organisation's formula is missing or not one this form can fill. */
    parts: FormulaPart[] | null;
    options: CommunityOption[];
    keyCounters: Array<{ key: string; next: number }>;
}

export const useCommunityData = (): CommunityData => {
    const { data: formulaData, loading: formulaLoading } = useQuery(GET_FORMULA);
    const parts = parseFormula(formulaData?.me?.organisation?.personIdentifierFormula);

    const groupId = parts != null ? communityGroupOf(parts) : null;
    const keyGroupId = parts?.find((p) => p.kind === 'key')?.groupId ?? null;

    const { data: tagData, loading: tagsLoading } = useQuery(GET_COMMUNITIES, {
        variables: { groupId },
        skip: groupId == null,
    });
    const { data: keyData, loading: keysLoading } = useQuery(GET_KEY_COUNTERS, {
        variables: { groupId: keyGroupId },
        skip: keyGroupId == null,
        // The next number moves every time anyone adds a person; a cached one is a refused save.
        fetchPolicy: 'network-only',
    });

    return {
        loading: formulaLoading || tagsLoading || keysLoading,
        parts,
        options: (tagData?.tagsForGroup ?? []).map((t: any) => ({
            id: t.id, code: t.code, name: t.name, isLocation: !!t.isLocation,
        })),
        keyCounters: keyData?.keyCounterLookup ?? [],
    };
};

/** Resolve the picked ids (in pick order) into the coding choice and the identifier it gives. */
export const resolveCommunities = (
    data: CommunityData,
    selectedIds: number[],
    homeLocationId: number | null,
): { coding: CodingChoice; identifier: string; selected: CommunityOption[] } => {
    const byId = new Map(data.options.map((o) => [o.id, o]));
    const selected = selectedIds.map((id) => byId.get(id)).filter((o): o is CommunityOption => o != null);
    const coding = chooseCodingCommunity(selected, homeLocationId);
    const identifier = coding.status === 'ok' && data.parts != null
        ? buildIdentifier(data.parts, coding.community.code, data.keyCounters)
        : '';

    return { coding, identifier, selected };
};

export const CommunityPicker = ({
    data,
    selectedIds,
    onSelectedIdsChange,
    homeLocationId,
    onHomeLocationIdChange,
    showErrors,
}: {
    data: CommunityData;
    selectedIds: number[];
    onSelectedIdsChange: (ids: number[]) => void;
    homeLocationId: number | null;
    onHomeLocationIdChange: (id: number) => void;
    showErrors: boolean;
}) => {
    if (data.loading) return <p>Loading communities…</p>;

    if (data.parts == null) {
        return (
            <p className="creator-section-hint">
                This organisation has no identifier formula this form can use, so people can't be added
                here. Use the classic form.
            </p>
        );
    }

    const { coding, identifier, selected } = resolveCommunities(data, selectedIds, homeLocationId);
    const locations = selected.filter((c) => c.isLocation);

    return (
        <>
            <FormField error={showErrors && coding.status === 'none'}>
                <label htmlFor="registration-communities">Communities</label>
                <Dropdown
                    id="registration-communities"
                    placeholder="Every community they belong to"
                    fluid
                    multiple
                    search
                    selection
                    options={data.options.map((o) => ({ key: o.id, text: o.name, value: o.id }))}
                    value={selectedIds}
                    onChange={(_, d) => onSelectedIdsChange((d.value as number[]) ?? [])}
                />
                <div className="consent-hint">
                    Pick every community, in any order. Their identifier is coded from their location —
                    Joyya Goods or another programme only codes it when they have no location.
                </div>
            </FormField>

            {/* Two locations: ask, don't refuse — someone can live between two. The radios stay
                while there are two, so the choice can be changed, not just made. */}
            {locations.length > 1 && (
                <FormField error={showErrors && coding.status === 'chooseHome'}>
                    <label>Which is their home location?</label>
                    {locations.map((l) => (
                        <Radio
                            key={l.id}
                            id={`home-location-${l.id}`}
                            label={l.name}
                            name="homeLocation"
                            checked={coding.status === 'ok' && coding.community.id === l.id}
                            onChange={() => onHomeLocationIdChange(l.id)}
                            style={{ marginRight: '1.5em' }}
                        />
                    ))}
                </FormField>
            )}

            <FormField>
                <label htmlFor="registration-identifier">Identifier</label>
                <Input
                    id="registration-identifier"
                    fluid
                    readOnly
                    className="identifier-display"
                    value={identifier}
                    placeholder="(Allocated once a community is picked)"
                />
                {coding.status === 'ok' && (
                    <div className="consent-hint">Coded from {coding.community.name}.</div>
                )}
            </FormField>
        </>
    );
};
